/* Reusable button component with brand variants (primary, secondary, danger, ghost),
   sizes, loading state, icon support, and icon-only mode with enforced aria-label. */
import { forwardRef, type ButtonHTMLAttributes } from 'react';
import { clsx } from 'clsx';
import LoadingSpinner from './LoadingSpinner';

type ButtonBaseProps = {
  variant?: 'primary' | 'secondary' | 'danger' | 'ghost';
  size?: 'sm' | 'md' | 'lg';
  loading?: boolean;
  icon?: React.ReactNode;
};

type StandardButtonProps = ButtonBaseProps & {
  iconOnly?: false;
} & ButtonHTMLAttributes<HTMLButtonElement>;

type IconOnlyButtonProps = ButtonBaseProps & {
  iconOnly: true;
  'aria-label': string;
} & ButtonHTMLAttributes<HTMLButtonElement>;

export type ButtonProps = StandardButtonProps | IconOnlyButtonProps;

const variantClasses = {
  primary: 'btn-primary',
  secondary: 'btn-secondary',
  danger: 'btn-danger',
  ghost: 'btn-ghost',
};

const sizeClasses = {
  sm: 'px-3 py-1.5 text-body-sm',
  md: '',
  lg: 'px-6 py-3',
};

const iconSizeClasses = {
  sm: 'btn-icon-sm',
  md: 'btn-icon-md',
  lg: 'btn-icon-lg',
};

const iconVariantClasses = {
  primary: 'btn-icon-ghost',
  secondary: 'btn-icon-ghost',
  danger: 'btn-icon-danger',
  ghost: 'btn-icon-ghost',
};

const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      children,
      variant = 'primary',
      size = 'md',
      loading = false,
      icon,
      iconOnly,
      className,
      disabled,
      ...props
    },
    ref
  ) => {
    return (
      <button
        ref={ref}
        disabled={disabled || loading}
        className={clsx(
          iconOnly
            ? [iconSizeClasses[size], iconVariantClasses[variant]]
            : [variantClasses[variant], sizeClasses[size]],
          className
        )}
        {...props}
      >
        {iconOnly ? (
          loading ? (
            <LoadingSpinner size="sm" />
          ) : (
            icon
          )
        ) : (
          <>
            {loading ? (
              <LoadingSpinner size="sm" className="mr-2" />
            ) : icon ? (
              <span className="mr-2">{icon}</span>
            ) : null}
            {children}
          </>
        )}
      </button>
    );
  }
);

Button.displayName = 'Button';

export default Button;
